import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import moment from "moment";
import useReload from "../../hooks/useReload";
import useApp from "../../hooks/useApp";
import ClientClaimRequirement from "../policies/ClientClaimRequirement";
import ClaimItemStatus from "../../widgets/ClaimItemStatus";
import {
  Accordion,
  Button,
  Card,
  Modal,
  Container,
  Form,
  InputGroup,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import { NavigateBackButton } from "../../widgets";
import Swal from "sweetalert2";

const ClientAccountClaimsView = () => {
  const navigate = useNavigate();
  const { claimId = null } = useParams();

  const [claim, setClaim] = useState({});
  const [policyItem, setPolicyItem] = useState({});
  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();

  const getClaim = async () => {
    try {
      if (claimId) {
        const { data = {} } = await api.read(
          `/client-accounts/claims/${claimId}`
        );

        setClaim(data.claim);

        setPolicyItem(data.policyItem);
      }
    } catch (error) {}
  };

  const handleClaimDelete = async () => {
    const deleteClaim = async () => {
      const response = await api.delete(`/claims/${claim?.id}`);

      if (response.ok) {
        showAlert("Claim deleted", "success");
        navigate(`/client-account/claims`, { replace: true });
      }
    };
    Swal.fire({
      title: "Delete Claim",
      text: "Are you sure you want to delete this claim?",
      icon: "warning",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteClaim();
      }
    });
  };

  useEffect(() => {
    getClaim();
  }, [auth, claimId]);

  return (
    <>
      <section id="content" className="content">
        <div className="content__header content__boxed">
          <div className="content__wrap">
            {/* <!-- Breadcrumb --> */}
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/client-policies">My Claims</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Detail
                </li>
              </ol>
            </nav>
            {/* <!-- END : Breadcrumb --> */}

            <h1 className="page-title mb-0 mt-2">Claim View</h1>

            <p className="lead">View claim detail</p>

            {claim && (
              <div className="d-md-flex align-items-baseline mt-3">
                <h2 className="me-2">
                  <ClaimItemStatus status={claim?.stage} />
                </h2>
                <Button
                  variant="info me-2"
                  size="xs"
                  onClick={() => navigate(`/client-account/claims/`)}
                >
                  <i className="bi bi-exclamation-circle fs-5 me-2" />
                  Claims
                </Button>

                {claim?.stage === 1 && claim?.createdBy === auth?.user?.id && (
                  <Button
                    variant="danger me-2"
                    size="xs"
                    onClick={() => handleClaimDelete()}
                  >
                    <i className="bi bi-trash fs-5 me-2" />
                    Delete Claim
                  </Button>
                )}

                <div className="d-flex align-items-center gap-1 text-nowrap ms-auto mb-3">
                  <NavigateBackButton />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="content__boxed">
          <div className="content__wrap">
            <Row className="mb-3">
              <Col sm={12} md={6}>
                <Card>
                  <Card.Header>Claim</Card.Header>
                  <Card.Body>
                    <Table bordered striped="columns">
                      <tbody>
                        <tr>
                          <td>Incident Date</td>
                          <td>
                            {moment(claim?.incidentDate).format(
                              "DD-MM-YYYY, h:mm a"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Report Date</td>
                          <td>
                            {moment(claim?.reportDate).format("DD-MM-YYYY")}
                          </td>
                        </tr>
                        <tr>
                          <td>Incident Description</td>
                          <td>
                            <pre>{claim?.incidentDescription}</pre>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={12} md={6}>
                <Card>
                  <Card.Header>Policy</Card.Header>
                  <Card.Body>
                    <Table bordered striped="columns">
                      <tbody>
                        <tr>
                          <td>Info</td>
                          <td>
                            <Link
                              to={`/client-account/policy-item/${policyItem?.id}`}
                            >
                              {claim?.ClientPolicy?.title}
                            </Link>
                          </td>
                        </tr>
                        <tr>
                          <td>Policy Number</td>
                          <td>{claim?.ClientPolicy?.policyNumber}</td>
                        </tr>
                        <tr>
                          <td>Policy type</td>
                          <td>
                            {claim?.ClientPolicy?.PolicyType?.name} <br />{" "}
                            {claim?.ClientPolicy?.Product?.name}
                          </td>
                        </tr>
                        <tr>
                          <td>Insuring Company</td>
                          <td>{claim?.ClientPolicy?.Company?.name}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Accordion defaultActiveKey="0" className="mb-2">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Claim Requirements</Accordion.Header>
                <Accordion.Body>
                  <ClientClaimRequirement
                    claim={claim}
                    hideSave={true}
                    policy={claim?.ClientPolicy}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
};

export default ClientAccountClaimsView;
